import React from 'react';
import {
    Row, Col, Select, Button, Space, Typography, InputNumber, Tag, Empty, Popconfirm, Input, Card,
} from 'antd';
import { PlusOutlined, DeleteOutlined, WarningOutlined } from '@ant-design/icons';
import { BUNDLES, BUNDLE_BY_KEY, isCompanyBundle } from './featureAccess';
import useBundleFeatures from './useBundleFeatures';
import FeatureTags from './FeatureTags';

const { Text } = Typography;
// Per-user (seat) bundles only. Company-scoped bundles (ADR-033) are toggled from the
// Companies page AI card and shown here read-only, so the license record stays complete.
const bundleOptions = BUNDLES.filter((b) => !isCompanyBundle(b.key)).map((b) => ({ label: b.label, value: b.key }));

const LicenseCard = ({ license, used, onChange, onRemove, featuresForBundles }) => {
    const available = license.seatCount - used;
    const over = used > license.seatCount;
    const seatBundles = (license.bundles || []).filter((bk) => !isCompanyBundle(bk));
    const companyBundles = (license.bundles || []).filter(isCompanyBundle);
    // The multi-select edits seat bundles only; company-scoped ones ride along untouched.
    const onSeatBundlesChange = (vals) => onChange({ bundles: [...vals, ...companyBundles] });
    return (
        <div style={{ border: '1px solid var(--gh-gray-200, #e6e6e6)', borderRadius: 8, padding: 14, marginBottom: 12, background: '#fafafa' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <Input
                    variant="borderless"
                    defaultValue={license.name}
                    onBlur={(e) => e.target.value !== license.name && onChange({ name: e.target.value })}
                    style={{ fontWeight: 600, paddingLeft: 0, flex: 1 }}
                />
                <Popconfirm title="Remove this license?" onConfirm={onRemove} okText="Remove" okButtonProps={{ danger: true }}>
                    <Button type="text" danger size="small" icon={<DeleteOutlined />} />
                </Popconfirm>
            </div>
            <Row gutter={[16, 12]} align="top">
                <Col xs={24} sm={8}>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Seats</Text>
                    <Space>
                        <InputNumber min={0} value={license.seatCount} onChange={(v) => onChange({ seatCount: v || 0 })} />
                        <Tag color={over ? 'error' : 'default'} icon={over ? <WarningOutlined /> : null}>
                            {used} used {over ? `· ${used - license.seatCount} over` : `· ${available} free`}
                        </Tag>
                    </Space>
                    {over && <div style={{ marginTop: 4 }}><Text type="danger" style={{ fontSize: 11 }}>Over capacity — allowed, but review seats.</Text></div>}
                </Col>
                <Col xs={24} sm={16}>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Offered bundles</Text>
                    <Select
                        mode="multiple"
                        style={{ width: '100%' }}
                        value={seatBundles}
                        onChange={onSeatBundlesChange}
                        options={bundleOptions}
                        placeholder="Bundles this license offers to users"
                        maxTagCount="responsive"
                    />
                    {companyBundles.length > 0 && (
                        <div style={{ marginTop: 6 }}>
                            <Text type="secondary" style={{ fontSize: 11, marginRight: 6 }}>Company-wide:</Text>
                            {companyBundles.map((bk) => (
                                <Tag key={bk} color="purple" style={{ fontSize: 11 }}>
                                    {BUNDLE_BY_KEY[bk]?.label || bk} · no seat · toggle on the AI card
                                </Tag>
                            ))}
                        </div>
                    )}
                    <div style={{ marginTop: 6 }}>
                        <Text type="secondary" style={{ fontSize: 11, marginRight: 6 }}>Grants:</Text>
                        <FeatureTags featureKeys={featuresForBundles(license.bundles)} empty="no bundles" />
                    </div>
                </Col>
            </Row>
        </div>
    );
};

// Presentational: all state/handlers come from the parent (useCompanyAccess).
const LicensesPanel = ({
    licenses = [], catalog = [], usedFor, onAddFromPlan, onAddCustom, onUpdate, onRemove, bare = false,
}) => {
    // The live bundle→features map (DB overrides, not the config defaults), so
    // the "Grants" chips reflect a customized bundle.
    const { featuresForBundles } = useBundleFeatures();

    const body = (
        <>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 14 }}>
                A license is a plan this company holds, with a seat count and a menu of bundles. Assign users to
                seats via each user’s <b>Access</b> action. Seats are tracked and warned on, never blocked.
            </Text>
            {licenses.length === 0
                ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No licenses yet — add one from a plan above" />
                : licenses.map((l) => (
                    <LicenseCard
                        key={l.id}
                        license={l}
                        used={usedFor(l.id)}
                        onChange={(patch) => onUpdate(l.id, patch)}
                        onRemove={() => onRemove(l.id)}
                        featuresForBundles={featuresForBundles}
                    />
                ))}
        </>
    );

    const toolbar = (
        <Space>
            <Select
                style={{ minWidth: 260 }}
                placeholder="Add license from a plan…"
                value={null}
                onChange={(v) => onAddFromPlan(v)}
                options={catalog.map((p) => ({ label: p.price != null ? `${p.name} ($${p.price})` : p.name, value: p.id }))}
                optionFilterProp="label"
                showSearch
            />
            <Button icon={<PlusOutlined />} onClick={onAddCustom}>Blank</Button>
        </Space>
    );

    if (bare) return <div>{toolbar}<div style={{ marginTop: 12 }}>{body}</div></div>;
    return <Card className="gh-dash-card" title="Licenses" extra={toolbar}>{body}</Card>;
};

export default LicensesPanel;
